import { Component, type ReactNode } from 'react'
import { TriangleAlert } from 'lucide-react'

interface Props {
  children: ReactNode
  /** Current route. When it changes after an error, we auto-recover (the user navigated away). */
  pathname: string
  /** Navigate home — wired to the recovery button. */
  onGoHome: () => void
}
interface State {
  error: Error | null
}

/**
 * App-level error boundary. A render/commit error anywhere below this (e.g. a Mapbox teardown
 * throw while leaving the full-screen map) would otherwise unmount the whole React root and
 * leave a permanent blank screen with no way back. Here we catch it and recover:
 *  • RETRY ONCE — most transition-time errors (e.g. the OUTGOING route's effect cleanup throwing
 *    as we arrive at a new one) are one-shot: the destination route renders fine on a retry. We
 *    schedule a single automatic retry; if it throws again the route is genuinely broken and we
 *    keep the fallback (the retry latch prevents a render loop).
 *  • NAVIGATE AWAY — any later route change also clears the error.
 *  • MANUAL — the fallback always offers a guaranteed route home.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null }
  /** Latches after one auto-retry so a route that keeps throwing can't loop. */
  private retried = false

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error) {
    // Keep a breadcrumb for diagnostics; the boundary itself keeps the app usable.
    console.error('[app] render error caught by RouteErrorBoundary:', error)
  }

  componentDidUpdate(prev: Props, prevState: State) {
    // A route change after an error means we've moved off the broken view — drop the error so
    // the new route renders instead of the fallback.
    if (this.state.error && prev.pathname !== this.props.pathname) {
      this.setState({ error: null })
      return
    }
    // A freshly-caught error: try recovering once on the next tick. Transition/teardown throws
    // surface while we're already on the destination route (so the pathname check above can't
    // catch them), but the destination itself is healthy and re-renders cleanly.
    if (this.state.error && !prevState.error && !this.retried) {
      this.retried = true
      queueMicrotask(() => this.setState({ error: null }))
    }
    // Healthy again → re-arm the retry for any future error.
    if (!this.state.error && prevState.error) {
      this.retried = false
    }
  }

  private recover = () => {
    this.retried = false
    this.setState({ error: null })
    this.props.onGoHome()
  }

  render() {
    if (this.state.error) {
      return (
        <div className="app-error" role="alert">
          <TriangleAlert size={32} className="app-error__icon" />
          <p className="app-error__title">Something went wrong</p>
          <p className="app-error__sub">This screen hit an unexpected error. Your saved data is safe.</p>
          <button type="button" className="app-error__btn" onClick={this.recover}>
            Back to home
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
